import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from 'react';
import { DEFAULT_SECTION_ACTIVATION_THRESHOLD, SECTION_SCROLL_RESTORATION_DELAY, SMOOTH_SCROLL_BUFFER } from '@/modules/shared/lib/constants';
import { decodeSectionId } from './section-id-utils';
interface UseSectionActivationOptions {
  /**
   * The threshold percentage from the top of the viewport to activate the section.
   *
   * @default 20
   */
  threshold?: number;

  /**
   * The section to skip from the activation. When this section is activated, the hash will be cleared.
   *
   * @default undefined
   */
  skipSection?: string;
}
const defaultOptions = {
  threshold: DEFAULT_SECTION_ACTIVATION_THRESHOLD
};
const useSectionActivation = (sections, t0) => {
  const $ = _c(11);
  const options = t0 === undefined ? defaultOptions : t0;
  const t1 = options.threshold ?? defaultOptions.threshold;
  let t2;
  if ($[0] !== options.skipSection || $[1] !== t1) {
    t2 = {
      threshold: t1,
      skipSection: options.skipSection
    };
    $[0] = options.skipSection;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const initializedOptions = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(_temp2, t3);
  const isRestoringScrollRef = useRef(false);
  let t4;
  let t5;
  if ($[4] !== initializedOptions || $[5] !== sections) {
    t4 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const topMargin = -initializedOptions.threshold;
      const bottomMargin = -(100 - initializedOptions.threshold - 0.01);
      const rootMargin = `${topMargin}% 0px ${bottomMargin}% 0px`;
      let activeSection = null;
      const observer = new IntersectionObserver(entries => {
        if (isRestoringScrollRef.current) {
          return;
        }
        const intersecting = entries.filter(_temp3);
        if (intersecting.length > 0) {
          const sortedIntersecting = intersecting.sort(_temp4);
          const topSection = decodeSectionId(sortedIntersecting[0].target.id);
          if (activeSection !== topSection) {
            activeSection = topSection;
            if (initializedOptions.skipSection && topSection === initializedOptions.skipSection) {
              const basePath = window.location.pathname + window.location.search;
              window.history.replaceState(null, "", basePath);
            } else {
              const basePath_0 = window.location.pathname + window.location.search;
              const newUrl = `${basePath_0}#${topSection}`;
              window.history.replaceState(null, "", newUrl);
            }
          }
        }
      }, {
        rootMargin,
        threshold: 0
      });
      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          observer.observe(element);
        }
      });
      return () => {
        observer.disconnect();
      };
    };
    t5 = [initializedOptions, sections];
    $[4] = initializedOptions;
    $[5] = sections;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== sections) {
    t6 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const hash = window.location.hash.slice(1);
      if (hash) {
        const activeSectionId = sections.find(section_0 => decodeSectionId(section_0).includes(hash));
        if (activeSectionId) {
          const element_0 = document.getElementById(activeSectionId);
          if (element_0) {
            isRestoringScrollRef.current = true;
            let clearFlagTimeout;
            const scrollTimeout = setTimeout(() => {
              element_0.scrollIntoView({
                behavior: "smooth"
              });
              clearFlagTimeout = setTimeout(() => {
                isRestoringScrollRef.current = false;
              }, SMOOTH_SCROLL_BUFFER);
            }, SECTION_SCROLL_RESTORATION_DELAY);
            return () => {
              clearTimeout(scrollTimeout);
              if (clearFlagTimeout) {
                clearTimeout(clearFlagTimeout);
              }
              isRestoringScrollRef.current = false;
            };
          }
        }
      }
    };
    t7 = [sections];
    $[8] = sections;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
};
export { useSectionActivation, type UseSectionActivationOptions };
function _temp() {
  history.scrollRestoration = "auto";
}
function _temp2() {
  if (typeof history === "undefined") {
    return;
  }
  history.scrollRestoration = "manual";
  return _temp;
}
function _temp3(entry) {
  return entry.isIntersecting;
}
function _temp4(a, b) {
  const aTop = (a.target as HTMLElement).getBoundingClientRect().top;
  const bTop = (b.target as HTMLElement).getBoundingClientRect().top;
  return aTop - bTop;
}